import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {Observable, throwError} from 'rxjs';
import {Injectable} from '@angular/core';
import {environment} from 'src/environments/environment';
import {catchError, map, switchMap, take} from 'rxjs/operators';
import {EventsService} from './events.service';
import {AuthService} from './auth.service';
import {AppStateService} from './app-state.service';
import {VetineoApiResponse} from '../models/vetineo-api-response.model';
import {CommonUtils} from "../utils/common";

/**
 * Główny serwis komunikacyjny (Wrapper) dla zapytań HTTP.
 * Automatyzuje proces sprawdzania stanu sieci, wstrzykiwania tokenów autoryzacyjnych,
 * budowania adresów URL oraz globalnego przechwytywania i rozgłaszania błędów API.
 */
@Injectable({
  providedIn: 'root'
})
export class ApiService {

  constructor(
    private http: HttpClient,
    private events: EventsService,
    private authService: AuthService,
    private appStateService: AppStateService,
    public commonUtils : CommonUtils
  ) {
  }

  /**
   * Wykonuje zapytanie HTTP GET do serwera API.
   * Przed wysłaniem weryfikuje połączenie z siecią i dokleja token autoryzacyjny.
   *
   * @param url Względna ścieżka do endpointu (np. '/users/1').
   * @param options Opcjonalne parametry zapytania i nagłówki.
   * @returns Observable emitujący ustandaryzowaną odpowiedź VetineoApiResponse.
   */
  get<VetineoApiResponse>(url: string, options?: {
    headers?: HttpHeaders | { [header: string]: string | string[]; };
    params?: HttpParams | { [param: string]: string | string[]; };
    reportProgress?: boolean;
    withCredentials?: boolean;
  }): Observable<VetineoApiResponse> {
    return this.appStateService.networkStatusSubject.pipe(take(1), switchMap(status => {
      if (status?.connected) {
        return this.authService.userAuthData.pipe(take(1), switchMap(userAuthData => {
          if (userAuthData && userAuthData.token) {
            if (!options) { options = {headers: new HttpHeaders().append('token', userAuthData.token)}; }
            else {
              options.headers = new HttpHeaders().append('token', userAuthData.token);
            }
          }
          return this.http.get<VetineoApiResponse>(`${environment.VETINEO_API_WS_URL}${url}`, options).pipe(
            catchError(errorResponse => {console.log(errorResponse)
              this.events.publish('errors:vetineoApi', errorResponse);
              return throwError(errorResponse);
            }),
          );
        }));
      } else {
        return throwError('You are offline');
      }
    }));
  }

  /**
   * Wykonuje zapytanie HTTP POST do serwera API (tworzenie zasobów).
   *
   * @param url Względna ścieżka do endpointu.
   * @param body Ładunek danych (Payload) wysyłany w ciele zapytania.
   * @param options Opcjonalne parametry zapytania i nagłówki.
   * @returns Observable emitujący ustandaryzowaną odpowiedź VetineoApiResponse.
   */
  post<VetineoApiResponse>(url: string, body: any | null, options?: {
    headers?: HttpHeaders | { [header: string]: string | string[]; };
    params?: HttpParams | { [param: string]: string | string[]; };
    reportProgress?: boolean;
    withCredentials?: boolean;
    responseType?: 'arraybuffer' | 'blob' | 'json' | 'text';
  }): Observable<VetineoApiResponse> {
    return this.appStateService.networkStatusSubject.pipe(take(1), switchMap(status => {
      if (status.connected) {
        return this.authService.getUserAuthData().pipe(take(1), switchMap(userAuthData => {
          if (userAuthData && userAuthData.token) {
            if (!options) { options = {headers: new HttpHeaders().append('token', userAuthData.token)}; }
            else {
              options.headers = new HttpHeaders().append('token', userAuthData.token);
            }
          }
          return this.http.post<VetineoApiResponse>(`${environment.VETINEO_API_WS_URL}${url}`, body, options as { responseType?: 'json' }).pipe(
            catchError(errorResponse => {
              this.events.publish('errors:vetineoApi', errorResponse);
              return throwError(errorResponse);
            })
          );
        }));
      } else {
        throwError('You are offline');
      }
    }));
  }

  /**
   * Wykonuje zapytanie HTTP PUT do serwera API (aktualizacja zasobów).
   *
   * @param url Względna ścieżka do endpointu.
   * @param body Zaktualizowany ładunek danych (Payload).
   * @param options Opcjonalne parametry zapytania i nagłówki.
   * @returns Observable emitujący ustandaryzowaną odpowiedź VetineoApiResponse.
   */
  put<VetineoApiResponse>(url: string, body: any | null, options?: {
    headers?: HttpHeaders | { [header: string]: string | string[]; };
    params?: HttpParams | { [param: string]: string | string[]; };
    reportProgress?: boolean;
    withCredentials?: boolean;
  }): Observable<VetineoApiResponse> {
    return this.appStateService.networkStatusSubject.pipe(take(1), switchMap(status => {
      if (status.connected) {
        return this.authService.userAuthData.pipe(take(1), switchMap(userAuthData => {
          if (userAuthData && userAuthData.token) {
            if (!options) { options = {headers: new HttpHeaders().append('token', userAuthData.token)}; }
            else {
              options.headers = new HttpHeaders().append('token', userAuthData.token);
            }
          }
          return this.http.put<VetineoApiResponse>(`${environment.VETINEO_API_WS_URL}${url}`, body, options).pipe(
            catchError(errorResponse => {
              this.events.publish('errors:vetineoApi', errorResponse);
              return throwError(errorResponse);
            })
          );
        }));
      } else {
        throwError('You are offline');
      }
    }));
  }

  /**
   * Wykonuje zapytanie HTTP DELETE do serwera API (usuwanie zasobów).
   *
   * @param url Względna ścieżka do endpointu zasobu do usunięcia.
   * @param options Opcjonalne parametry zapytania i nagłówki.
   * @returns Observable emitujący ustandaryzowaną odpowiedź VetineoApiResponse.
   */
  delete<VetineoApiResponse>(url: string, options?: {
    headers?: HttpHeaders | { [header: string]: string | string[]; };
    params?: HttpParams | { [param: string]: string | string[]; };
    reportProgress?: boolean;
    withCredentials?: boolean;
  }): Observable<VetineoApiResponse> {
    return this.appStateService.networkStatusSubject.pipe(take(1), switchMap(status => {
      if (status.connected) {
        return this.authService.userAuthData.pipe(take(1), switchMap(userAuthData => {
          if (userAuthData && userAuthData.token) {
            if (!options) { options = {headers: new HttpHeaders().append('token', userAuthData.token)}; }
            else {
              options.headers = new HttpHeaders().append('token', userAuthData.token);
            }
          }
          return this.http.delete<VetineoApiResponse>(`${environment.VETINEO_API_WS_URL}${url}`, options).pipe(
            catchError(errorResponse => {
              this.events.publish('errors:vetineoApi', errorResponse);
              return throwError(errorResponse);
            })
          );
        }));
      } else {
        throwError('You are offline');
      }
    }));
  }

  /**
   * Dedykowana metoda weryfikująca ważność i uprawnienia aktualnego tokenu sesji.
   *
   * @returns Observable emitujący dane autoryzacyjne użytkownika.
   */
  validateAuthToken(): Observable<any> {
    return this.get<VetineoApiResponse>(`/auth/check-token-dashboard`).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok && vetineoApiResponse.response) {
        return vetineoApiResponse.response
      }
    }));
  }
}
